import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useProfile } from "@/hooks/useAuth";
import { useIsSolo } from "@/hooks/useIsSolo";
import { fetchAccounts, fetchMembers } from "@/lib/queries";
import {
  fetchIous,
  fetchIouCurrencies,
  computeBalances,
  iousWithoutCurrency,
  fetchIouPurchases,
  iouPurchaseIds,
  presentIouDetail,
  iouDetailText,
  iouSearchValidator,
  IOU_TABS,
  type IouHeader,
  type IouTab,
} from "@/lib/iou";
import { iousFormingBalance } from "@/lib/household-outlook";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Handshake, ArrowRight, Banknote, Gift } from "lucide-react";
import { IouDialog } from "@/components/IouDialog";
import { IouSettleDialog } from "@/components/IouSettleDialog";
import { HouseholdMonthlyStatementCard } from "@/components/HouseholdMonthlyStatementCard";
import { HouseholdFairShareCard } from "@/components/HouseholdFairShareCard";
import { HouseholdLedgerBookCard } from "@/components/HouseholdLedgerBookCard";
import { useFormatCurrency } from "@/hooks/useFormatCurrency";
import { accountsQueryKey } from "@/lib/authScopedQueryKeys";

export const Route = createFileRoute("/_app/iou")({
  validateSearch: iouSearchValidator,
  component: IouPage,
});

function IouPage() {
  const navigate = useNavigate();
  const { tab } = Route.useSearch();
  const formatCurrency = useFormatCurrency();
  const { profile } = useProfile();
  const { isSolo } = useIsSolo();
  const hh = profile?.household_id;
  const profileId = profile?.id;
  const iouQ = useQuery({ queryKey: ["ious", hh], queryFn: () => fetchIous(hh!), enabled: !!hh });
  // La moneda de cada préstamo sale del RPC (igual para los dos miembros), no de las cuentas que ve cada uno.
  const curQ = useQuery({
    queryKey: ["iou-currencies", hh],
    queryFn: fetchIouCurrencies,
    enabled: !!hh,
  });
  const accQ = useQuery({
    queryKey: accountsQueryKey(hh ?? "", profileId ?? ""),
    queryFn: () => fetchAccounts(hh!),
    enabled: !!hh && !!profileId,
  });
  const memQ = useQuery({
    queryKey: ["members", hh],
    queryFn: () => fetchMembers(hh!),
    enabled: !!hh,
  });
  // Compras con tarjeta detrás de los préstamos (abiertos y saldados): el detalle sale de ahí.
  const purchaseIds = useMemo(() => iouPurchaseIds(iouQ.data ?? []), [iouQ.data]);
  const purchasesQ = useQuery({
    queryKey: ["iou-purchases", hh, purchaseIds.join(",")],
    queryFn: () => fetchIouPurchases(purchaseIds),
    enabled: !!hh && !!iouQ.data,
  });
  const [openNew, setOpenNew] = useState(false);
  const [settling, setSettling] = useState<IouHeader | null>(null);

  const currencyByIouId = curQ.data;
  const memMap = useMemo(
    () => new Map((memQ.data ?? []).map((m) => [m.id, m.display_name])),
    [memQ.data],
  );
  const accMap = useMemo(() => new Map((accQ.data ?? []).map((a) => [a.id, a])), [accQ.data]);

  // Detalle de un préstamo (compra con tarjeta): `null` si es manual/de banco y ya trae su descripción.
  const purchasesState = purchasesQ.isError
    ? ({ status: "error" } as const)
    : purchasesQ.data
      ? ({ status: "ready", byId: purchasesQ.data } as const)
      : ({ status: "loading" } as const);
  const detailOf = (i: IouHeader) => presentIouDetail(i, purchasesState);
  const detailTextOf = (i: IouHeader) => {
    const d = detailOf(i);
    return iouDetailText(d, {
      accountName: d.kind === "purchase" ? accMap.get(d.accountId)?.name : null,
      formatMoney: formatCurrency,
    });
  };

  const ious = iouQ.data ?? [];
  const open = ious.filter((i) => !i.settled_at);
  const settled = ious.filter((i) => i.settled_at);
  const noCurrency = useMemo(
    () => (currencyByIouId ? iousWithoutCurrency(open, currencyByIouId) : []),
    [open, currencyByIouId],
  );
  const balances = useMemo(
    () => (currencyByIouId ? computeBalances(open, currencyByIouId) : []),
    [open, currencyByIouId],
  );
  // La cabecera de la tarjeta suma "Entre miembros": si los préstamos o su moneda no se pudieron leer, o
  // hay un préstamo cuya moneda no se sabe, el número final no se da (nunca se asume "sin deudas" ni "MXN").
  const entreMiembrosState =
    iouQ.isError || curQ.isError || noCurrency.length > 0
      ? "error"
      : iouQ.data && curQ.data
        ? "ready"
        : "loading";

  if (isSolo) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold">Deudas del hogar</h1>
        <Card>
          <CardContent className="p-10 text-center text-sm text-muted-foreground">
            Deudas del hogar está disponible cuando hay dos miembros en el hogar.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Deudas del hogar</h1>
          <p className="text-sm text-muted-foreground">
            Tu saldo con el hogar y las deudas entre miembros.
          </p>
        </div>
        <Button onClick={() => setOpenNew(true)}>
          <Plus className="mr-2 h-4 w-4" /> Nueva deuda
        </Button>
      </div>

      <Tabs
        value={tab}
        onValueChange={(v) => {
          if ((IOU_TABS as readonly string[]).includes(v)) {
            void navigate({ to: "/iou", search: { tab: v as IouTab }, replace: true });
          }
        }}
      >
        <TabsList className="grid h-auto w-full grid-cols-3">
          <TabsTrigger value="resumen" className="whitespace-normal px-2 text-xs sm:text-sm">
            Resumen
          </TabsTrigger>
          <TabsTrigger value="mes" className="whitespace-normal px-2 text-xs sm:text-sm">
            Mes a mes
          </TabsTrigger>
          <TabsTrigger value="miembros" className="whitespace-normal px-2 text-xs sm:text-sm">
            Entre miembros
          </TabsTrigger>
        </TabsList>

        <TabsContent value="resumen" className="space-y-4">
          {/* Un solo número por persona: saldo proporcional con el hogar ± "Entre miembros". */}
          {hh && profileId && memQ.data && memQ.data.length > 0 && (
            <HouseholdFairShareCard
              householdId={hh}
              profileId={profileId}
              members={memQ.data}
              entreMiembros={{
                state: entreMiembrosState,
                pairs: balances,
                errorMessage:
                  noCurrency.length > 0 && !iouQ.isError && !curQ.isError
                    ? "No sabemos en qué moneda está algún préstamo entre miembros, así que todavía no podemos dar el número final."
                    : undefined,
                onRetry: () => {
                  void iouQ.refetch();
                  void curQ.refetch();
                },
              }}
            />
          )}
        </TabsContent>

        <TabsContent value="mes" className="space-y-4">
          {/* Estado de cuenta mensual del ledger miembro↔hogar (RPC acotado a cuentas del hogar). */}
          {hh && profileId && memQ.data && memQ.data.length > 0 && (
            <HouseholdMonthlyStatementCard
              householdId={hh}
              profileId={profileId}
              members={memQ.data}
            />
          )}

          {/* Libro por miembro: renglón por renglón, cómo se armó la deuda de cada uno (RPC de solo lectura). */}
          {hh && profileId && memQ.data && memQ.data.length > 0 && (
            <HouseholdLedgerBookCard householdId={hh} profileId={profileId} members={memQ.data} />
          )}
        </TabsContent>

        <TabsContent value="miembros" className="space-y-4">
          {/* IOUs interpersonales: no se mezclan con el ledger miembro↔hogar. */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Handshake className="h-4 w-4" /> Entre miembros
              </CardTitle>
            </CardHeader>
            <CardContent>
              {curQ.isError ? (
                // Sin la moneda no se puede armar el total: una falla no se muestra como "todo claro".
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <p className="text-muted-foreground">
                    No pudimos cargar la moneda de los préstamos.
                  </p>
                  <Button size="sm" variant="outline" onClick={() => void curQ.refetch()}>
                    Reintentar
                  </Button>
                </div>
              ) : !curQ.data ? (
                <p className="text-sm text-muted-foreground">Cargando…</p>
              ) : balances.length === 0 && noCurrency.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No hay deudas pendientes. ¡Todo claro! 🎉
                </p>
              ) : (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {noCurrency.length > 0 && (
                    <p className="text-xs text-muted-foreground sm:col-span-2">
                      No sabemos en qué moneda está{" "}
                      {noCurrency.length === 1 ? "un préstamo" : `${noCurrency.length} préstamos`}:
                      no se suma{noCurrency.length === 1 ? "" : "n"} en estos totales.
                    </p>
                  )}
                  {balances.map((b, i) => (
                    <div key={i} className="rounded-md border p-3 text-sm">
                      <span className="font-medium">{memMap.get(b.debtorId) ?? "—"}</span>
                      <span className="mx-1 text-muted-foreground">le debe a</span>
                      <span className="font-medium">{memMap.get(b.creditorId) ?? "—"}</span>
                      <div className="text-lg font-bold">
                        {formatCurrency(b.amount, b.currency)}
                      </div>
                      {/* Los préstamos que forman este total, a la vista junto al número. */}
                      <ul className="mt-2 space-y-1 border-t pt-2 text-xs">
                        {iousFormingBalance(open, b, currencyByIouId ?? new Map()).map(
                          ({ iou, sign }) => (
                            <li key={iou.id} className="flex items-baseline justify-between gap-3">
                              <span className="min-w-0">
                                <span className="text-muted-foreground">{iou.date}</span>{" "}
                                <span className="break-words">
                                  {detailTextOf(iou) ?? (iou.description || "Sin descripción")}
                                </span>
                                {accMap.get(iou.account_id ?? "")?.name && (
                                  <span className="text-muted-foreground">
                                    {" "}
                                    · desde {accMap.get(iou.account_id ?? "")?.name}
                                  </span>
                                )}
                              </span>
                              <span className="shrink-0 tabular-nums">
                                {sign === -1 && "−"}
                                {formatCurrency(Number(iou.total_amount), b.currency)}
                              </span>
                            </li>
                          ),
                        )}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Lista de IOUs abiertos */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Préstamos entre miembros ({open.length})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {open.length === 0 && (
                <p className="text-sm text-muted-foreground">No hay préstamos pendientes.</p>
              )}
              {open.map((i) => {
                const acc = accMap.get(i.account_id ?? "");
                const isHouseholdIou = acc?.is_household_account === true;
                const isBankExpenseIou = !!i.source_bank_expense_id;
                // Household IOUs: solo el deudor puede saldar (el acreedor no ve sus cuentas privadas).
                // Bank expense IOUs y manuales: ambos pueden abrir el dialog; el acreedor solo verá "Perdonar/Regalo".
                const canSettle = !isHouseholdIou || profile?.id === i.debtor_id;
                return (
                  <div
                    key={i.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3 text-sm"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          {memMap.get(i.creditor_id ?? "") ?? "—"}
                        </span>
                        <ArrowRight className="h-3 w-3 text-muted-foreground" />
                        <span className="font-medium">{memMap.get(i.debtor_id ?? "") ?? "—"}</span>
                        <Badge variant="outline" className="text-[10px]">
                          {i.date}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {acc?.name && <>desde {acc.name} · </>}
                        {detailTextOf(i) ?? (i.description || "Sin descripción")}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold">
                        {formatCurrency(Number(i.total_amount), acc?.currency ?? "MXN")}
                      </span>
                      {canSettle ? (
                        <Button size="sm" onClick={() => setSettling(i)}>
                          Saldar
                        </Button>
                      ) : (
                        <span className="text-xs text-amber-600 dark:text-amber-400 text-right max-w-[140px]">
                          Solo el deudor puede saldarla
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Saldados */}
          {settled.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Saldados entre miembros ({settled.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {settled.map((i) => {
                  const acc = accMap.get(i.account_id ?? "");
                  const Icon = i.settle_method === "forgive" ? Gift : Banknote;
                  return (
                    <div
                      key={i.id}
                      className="flex items-center justify-between rounded-md border p-3 text-sm opacity-70"
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <Icon className="h-3 w-3" />
                          <span className="font-medium">
                            {memMap.get(i.creditor_id ?? "") ?? "—"}
                          </span>
                          <ArrowRight className="h-3 w-3 text-muted-foreground" />
                          <span className="font-medium">
                            {memMap.get(i.debtor_id ?? "") ?? "—"}
                          </span>
                          <Badge variant="secondary" className="text-[10px]">
                            {i.settle_method === "forgive" ? "Perdón" : "Devuelto"}
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {i.date}
                          {" · "}
                          {detailTextOf(i) ?? (i.description || "Sin descripción")}
                        </div>
                      </div>
                      <span className="font-medium">
                        {formatCurrency(Number(i.total_amount), acc?.currency ?? "MXN")}
                      </span>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      <IouDialog open={openNew} onOpenChange={setOpenNew} />
      <IouSettleDialog
        open={!!settling}
        onOpenChange={(b) => {
          if (!b) setSettling(null);
        }}
        iou={settling}
        detailText={settling ? detailTextOf(settling) : null}
      />
    </div>
  );
}
